import { baseApi } from './baseApi'

import { BillingNoteDto, BillingNoteEntityType } from 'store/types'

type NoteTarget = {
  type: BillingNoteEntityType
  entityId: string
}

type AddNoteRequest = NoteTarget & {
  body: string
}

type EditNoteRequest = {
  noteId: string
  body: string
}

type RemoveNoteRequest = {
  noteId: string
}

// Notes on a charge, a payment and a payment transaction are the same record
// behind one set of routes — only `type` and `entityId` differ.
//
// The listings embed their own notes, so writing here invalidates those too:
// a note added from an expanded row has to change the count on the row.
const invalidatesListings = ['BillingNotes', 'BillingPatientPayments', 'BillingCharges', 'NeedsWork'] as const

export const billingNotesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBillingNotes: builder.query<BillingNoteDto[], NoteTarget>({
      query: ({ type, entityId }) => ({
        url: '/lab/billing/notes',
        params: { type, entityId },
      }),
      providesTags: ['BillingNotes'],
    }),
    addBillingNote: builder.mutation<{ noteId: string }, AddNoteRequest>({
      query: (body) => ({
        url: '/lab/billing/notes',
        method: 'POST',
        body,
      }),
      invalidatesTags: [...invalidatesListings],
    }),
    editBillingNote: builder.mutation<void, EditNoteRequest>({
      query: ({ noteId, body }) => ({
        url: `/lab/billing/notes/${noteId}`,
        method: 'PUT',
        body: { body },
      }),
      invalidatesTags: [...invalidatesListings],
    }),
    removeBillingNote: builder.mutation<void, RemoveNoteRequest>({
      query: ({ noteId }) => ({
        url: `/lab/billing/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [...invalidatesListings],
    }),
  }),
})

export const {
  useGetBillingNotesQuery,
  useAddBillingNoteMutation,
  useEditBillingNoteMutation,
  useRemoveBillingNoteMutation,
} = billingNotesApi
